import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EpcotConfig } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { CompetitionLogosService } from '@frontend/sports/competition-logos/feature';
import { LinkListLayout } from '@frontend/sports/types/components/widget/types';
import { LoadingIndicatorService } from '@frontend/vanilla/core';
import { keys } from 'lodash-es';
import { distinctUntilChanged, finalize, startWith } from 'rxjs';

import { PopupManager, PopupTypes } from '../popup/popup-manager.service';
import { CompetitionLinkItem, QuickLinkItem } from '../quick-links/quick-links.models';
import { PromoListItemSheetViewService } from './promo-list-item-sheetview.service';

@Component({
    selector: 'ms-promo-item-list',
    templateUrl: 'promo-item-list.component.html',
    styleUrl: './promo-item-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PromoItemListComponent implements OnInit, AfterViewInit {
    @Input() tracking?: string;
    @Input() arrow = true;
    @Input() items: QuickLinkItem[];
    @Input() itemClass = '';
    @Input() promotedCompetitionIdsMap = {};
    @Input() promotedSportIdsMap = {};
    @Input() layout?: LinkListLayout;
    @Output() itemClick = new EventEmitter<QuickLinkItem>();
    @ViewChild(ScrollAdapterComponent) scrollAdapter?: ScrollAdapterComponent;
    @ViewChildren('listItem', { read: ElementRef }) listItems!: QueryList<ElementRef>;

    cleanedAfterTrack = signal(false);
    private isEpcotEnabled: boolean;

    @HostBinding('class.mini-carousel-with-subheader') get isMiniCarouselLayout(): boolean {
        return this.layout === LinkListLayout.MiniCarousel && this.isEpcotEnabled;
    }

    @HostBinding('class.stacked-item-list') get isStackedList(): boolean {
        return this.layout === LinkListLayout.Stacked;
    }

    isStackedLayout = false;

    constructor(
        private competitionLogoService: CompetitionLogosService,
        private popupManager: PopupManager,
        private epcotConfig: EpcotConfig,
        private timer: TimerService,
        private destroyRef: DestroyRef,
        private loadingIndicatorService: LoadingIndicatorService,
        private promolistItemSVService: PromoListItemSheetViewService,
    ) {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    ngOnInit(): void {
        this.timer.setTimeout(() => this.scrollAdapter?.setArrows());
        this.isStackedLayout = this.layout === LinkListLayout.Stacked;
    }

    ngAfterViewInit(): void {
        this.registerQuickLinkPrefetch();
    }

    clicked(item: QuickLinkItem): void {
        this.cleanedAfterTrack.update(() => item.href?.indexOf('http') === 0 || false);
        this.itemClick.emit(item);

        if (item.quickLinkMode === 'sheetview') {
            const loadingIndicatorHandler = this.loadingIndicatorService.start();

            this.promolistItemSVService
                .fetchFixtureDataAndGrouping(item)
                .pipe(
                    finalize(() => loadingIndicatorHandler.done()),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(({ fixture, grouping }) => this.promolistItemSVService.openSheetView(fixture, grouping!, item.dynamicOfferCategory));
        } else if ('popup' in item && !!item.popup) {
            this.popupManager.toggle(<PopupTypes>item.popup);
        }
    }

    hasCompetitionLogo(item: QuickLinkItem): item is CompetitionLinkItem {
        if (item.sportId && item.leagueId && !item.disableLogoByLeagueAttribute) {
            return this.competitionLogoService.hasLogo(item.sportId, item.leagueId);
        }

        return false;
    }

    hasBgcolorForTilesOrGridView(item: QuickLinkItem): boolean {
        return !!(item.bgColor && this.layout && [LinkListLayout.TilesCarousel, LinkListLayout.Grid].includes(this.layout));
    }

    areBadgesEnabled(item: QuickLinkItem): boolean {
        if (!item.attributes) {
            return false;
        }

        const sportIds = item.attributes['promotedSportId']?.toString().split(',') || [];
        const leagueIds = item.attributes['promotedLeagueId']?.toString().split(',') || [];

        return (
            keys(this.promotedSportIdsMap).some((key) => sportIds.includes(key)) ||
            keys(this.promotedCompetitionIdsMap).some((key) => leagueIds.includes(key))
        );
    }

    itemIndexTrackBy(index: any, item: any): string {
        return index;
    }

    get isCarouselLayout(): boolean {
        return !!this.layout && [LinkListLayout.MiniCarousel, LinkListLayout.TilesCarousel].includes(this.layout);
    }

    private registerQuickLinkPrefetch(): void {
        this.listItems?.changes
            .pipe(startWith(this.listItems), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe((items: QueryList<ElementRef>) => {
                items.forEach((elRef, index) => {
                    const item = this.items[index];
                    if (item.quickLinkMode === 'sheetview') {
                        this.promolistItemSVService.registerQuickLinkPrefetch(elRef, item, this.destroyRef);
                    }
                });
            });
    }
}
